import React, { useState, useEffect } from 'react';
import CoachResourcesService, { RESOURCE_CATEGORIES, RESOURCE_TYPES } from '../../services/coachResourcesService';
import AddResourceModal from '../../components/AddResourceModal';
import AddContactModal from '../../components/AddContactModal';
import {
  BookOpenIcon,
  DocumentTextIcon,
  PhoneIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
  PlayIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  TagIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const CoachResourcesManagement = () => {
  const [resources, setResources] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resourcesData, contactsData, statsData] = await Promise.all([
        CoachResourcesService.getAllResources(),
        CoachResourcesService.getContacts(),
        CoachResourcesService.getResourceStatistics()
      ]);
      
      setResources(resourcesData);
      setContacts(contactsData);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading coach resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadData();
      return;
    }
    
    try {
      const searchResults = await CoachResourcesService.searchResources(searchQuery);
      setResources(searchResults);
    } catch (error) {
      console.error('Error searching resources:', error);
    }
  };

  const filteredResources = resources.filter(resource => {
    if (selectedCategory !== 'all' && resource.category !== selectedCategory) return false;
    return true;
  });

  const openResourceModal = (resource = null) => {
    setEditingResource(resource);
    setShowResourceModal(true);
  };

  const openContactModal = (contact = null) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };

  const closeResourceModal = () => {
    setShowResourceModal(false);
    setEditingResource(null);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setEditingContact(null);
  };

  const handleResourceSubmit = async (resourceData) => {
    console.log('Resource submitted with data:', resourceData);
    console.log('Editing resource:', editingResource);
    
    try {
      if (editingResource) {
        // Update existing resource
        console.log('Updating resource with ID:', editingResource.id);
        await CoachResourcesService.updateResource(editingResource.id, resourceData);
        console.log('Resource updated successfully');
      } else {
        // Create new resource
        console.log('Creating new resource');
        const result = await CoachResourcesService.createResource(resourceData);
        console.log('Resource created successfully:', result);
      }
      
      await loadData();
    } catch (error) {
      console.error('Error saving resource:', error);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  const handleContactSubmit = async (contactData) => {
    console.log('Contact submitted with data:', contactData);
    console.log('Editing contact:', editingContact);
    
    try {
      if (editingContact) {
        // Update existing contact
        console.log('Updating contact with ID:', editingContact.id);
        await CoachResourcesService.updateContact(editingContact.id, contactData);
        console.log('Contact updated successfully');
      } else {
        // Create new contact
        console.log('Creating new contact');
        const result = await CoachResourcesService.createContact(contactData);
        console.log('Contact created successfully:', result);
      }
      
      closeContactModal();
      await loadData();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Error saving contact. Please try again.');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
      return;
    }
    
    try {
      console.log(`Deleting ${type}:`, id);
      
      if (type === 'resource') {
        await CoachResourcesService.deleteResource(id);
        console.log('Resource deleted successfully');
      } else if (type === 'contact') {
        await CoachResourcesService.deleteContact(id);
        console.log('Contact deleted successfully');
      }
      
      await loadData();
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(`Error deleting ${type}. Please try again.`);
    }
  };

  const getTypeIcon = (type) => {
    const iconMap = {
      [RESOURCE_TYPES.DOCUMENT]: DocumentTextIcon,
      [RESOURCE_TYPES.LINK]: LinkIcon,
      [RESOURCE_TYPES.CONTACT]: PhoneIcon,
      [RESOURCE_TYPES.FORM]: ClipboardDocumentListIcon,
      [RESOURCE_TYPES.VIDEO]: PlayIcon,
      [RESOURCE_TYPES.GUIDE]: BookOpenIcon
    };
    return iconMap[type] || DocumentTextIcon;
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BuildingOfficeIcon },
    { id: 'resources', name: 'Manage Resources', icon: BookOpenIcon },
    { id: 'contacts', name: 'Manage Contacts', icon: PhoneIcon },
    { id: 'training', name: 'Training Management', icon: AcademicCapIcon }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading coach resources management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Coach Resources Management</h1>
              <p className="mt-2 text-gray-600">
                Manage TSSAA and DCCSL resources, training materials, and contact information
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => openResourceModal()}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add Resource</span>
              </button>
              <button
                onClick={() => openContactModal()}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add Contact</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BookOpenIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Resources</p>
                    <p className="text-2xl font-semibold text-gray-900">{statistics.totalResources || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Required Resources</p>
                    <p className="text-2xl font-semibold text-gray-900">{statistics.requiredResources || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <PhoneIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Contacts</p>
                    <p className="text-2xl font-semibold text-gray-900">{statistics.contacts || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AcademicCapIcon className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Training Resources</p>
                    <p className="text-2xl font-semibold text-gray-900">{statistics.trainingResources || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => openResourceModal()}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <BookOpenIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">Add New Resource</p>
                  <p className="text-xs text-gray-500">TSSAA, DCCSL, or training resource</p>
                </button>
                
                <button
                  onClick={() => openContactModal()}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
                >
                  <PhoneIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">Add New Contact</p>
                  <p className="text-xs text-gray-500">Important contact information</p>
                </button>
                
                <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">Bulk Import</p>
                  <p className="text-xs text-gray-500">Import multiple resources</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                <div className="flex-1 max-w-lg">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search resources..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Categories</option>
                    <option value={RESOURCE_CATEGORIES.TSSAA}>TSSAA</option>
                    <option value={RESOURCE_CATEGORIES.DCCSL}>DCCSL</option>
                    <option value={RESOURCE_CATEGORIES.RULES}>Rules</option>
                    <option value={RESOURCE_CATEGORIES.TRAINING}>Training</option>
                    <option value={RESOURCE_CATEGORIES.FORMS}>Forms</option>
                  </select>
                  
                  <button
                    onClick={handleSearch}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>

            {/* Resources Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Resources ({filteredResources.length})</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Resource
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredResources.map((resource) => {
                      const TypeIcon = getTypeIcon(resource.type);
                      return (
                        <tr key={resource.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <TypeIcon className="h-5 w-5 text-gray-400 mr-3" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">{resource.title}</div>
                                <div className="text-sm text-gray-500 truncate max-w-xs">{resource.description}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {resource.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {resource.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              CoachResourcesService.getPriorityColor(resource.priority)
                            }`}>
                              {resource.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {resource.required ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                Required
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                Optional
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openResourceModal(resource)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete('resource', resource.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="space-y-6">
            {/* Contacts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {contacts.map((contact) => (
                <div key={contact.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <PhoneIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{contact.title}</h3>
                        <p className="text-sm text-gray-500">{contact.organization}</p>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openContactModal(contact)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete('contact', contact.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {contact.contactName && (
                      <p><strong>Contact:</strong> {contact.contactName}</p>
                    )}
                    {contact.phone && (
                      <p><strong>Phone:</strong> {contact.phone}</p>
                    )}
                    {contact.email && (
                      <p><strong>Email:</strong> {contact.email}</p>
                    )}
                    {contact.hours && (
                      <p><strong>Hours:</strong> {contact.hours}</p>
                    )}
                  </div>
                  
                  {contact.services && contact.services.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-900 mb-2">Services:</p>
                      <div className="flex flex-wrap gap-1">
                        {contact.services.map((service, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'training' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Training Management</h2>
              <p className="text-gray-600 mb-6">
                Manage required training courses and certification requirements for coaches.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Training Management</h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      Training management features are coming soon. This will include tracking coach certifications, 
                      course completions, and renewal reminders.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Resource Modal */}
      <AddResourceModal
        isOpen={showResourceModal}
        onClose={closeResourceModal}
        onSubmit={handleResourceSubmit}
        editingResource={editingResource}
      />
      
      {/* Add Contact Modal */}
      <AddContactModal
        isOpen={showContactModal}
        onClose={closeContactModal}
        onSubmit={handleContactSubmit}
        editingContact={editingContact}
      />
    </div>
  );
};

export default CoachResourcesManagement;